import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { History, Clock, User, MessageSquare, ExternalLink, Trash2, AlertTriangle } from 'lucide-react';
import { formatDateTime } from '@/utils/date';
import { getUser, authenticatedFetch } from '@/lib/auth';

interface StudyPlanVersion {
  version_id: string;
  version_number: number;
  title: string;
  approved_by: string;
  approved_at: string;
  approver_name: string;
  change_comment: string | null;
  approval_link: string;
  deleted_at?: string | null;
  deleted_by?: string | null;
}

interface VersionHistoryProps {
  studyPlanId: string;
  userId: string;
  onVersionDeactivated?: () => void;
}

export function VersionHistory({ studyPlanId, userId, onVersionDeactivated }: VersionHistoryProps) {
  const navigate = useNavigate();
  const [versions, setVersions] = useState<StudyPlanVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<StudyPlanVersion | null>(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deactivating, setDeactivating] = useState(false);

  const currentUser = getUser();
  const isSystemAdmin = currentUser?.role === 'systemadmin';

  useEffect(() => {
    loadVersions();
  }, [studyPlanId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authenticatedFetch('/api/study-plans/versions/list', {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          study_plan_id: studyPlanId,
        }),
      });

      if (!response.ok) {
        throw new Error('Kunne ikke hente versjonsh istorikk');
      }

      const data = await response.json();

      if (data.success) {
        // Sort by version_number descending (newest first)
        const sortedVersions = (data.versions || []).sort(
          (a: StudyPlanVersion, b: StudyPlanVersion) =>
            b.version_number - a.version_number
        );
        setVersions(sortedVersions);
      } else {
        setError(data.error || 'Ukjent feil');
      }
    } catch (err) {
      console.error('Error loading versions:', err);
      setError(err instanceof Error ? err.message : 'Kunne ikke laste versjoner');
    } finally {
      setLoading(false);
    }
  };

  const getVerificationUrl = (approvalLink: string) => {
    const baseUrl = import.meta.env.VITE_BASE_URL || window.location.origin;
    return `${baseUrl}/verify/${approvalLink}`;
  };

  const handleDeactivateClick = (version: StudyPlanVersion) => {
    setSelectedVersion(version);
    setShowDeactivateModal(true);
  };

  const handleDeactivate = async () => {
    if (!selectedVersion || !currentUser) return;

    setDeactivating(true);
    setError(null);

    try {
      const response = await authenticatedFetch('/api/study-plans/soft-delete-version', {
        method: 'POST',
        body: JSON.stringify({
          admin_id: currentUser.id,
          version_id: selectedVersion.version_id,
          reason: deactivateReason.trim() || 'Deaktivert av systemadmin',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Kunne ikke deaktivere versjon');
      }

      // Reload versions to show updated status
      await loadVersions();

      // Notify parent component that a version was deactivated
      if (onVersionDeactivated) {
        onVersionDeactivated();
      }

      setShowDeactivateModal(false);
      setSelectedVersion(null);
      setDeactivateReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke deaktivere versjon');
    } finally {
      setDeactivating(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center space-x-3 mb-4">
          <History className="h-6 w-6 text-kf-primary" />
          <h2 className="text-xl font-semibold">Versjonshistorikk</h2>
        </div>
        <p className="text-gray-500">Laster versjoner...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center space-x-3 mb-4">
          <History className="h-6 w-6 text-kf-primary" />
          <h2 className="text-xl font-semibold">Versjonshistorikk</h2>
        </div>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center space-x-3 mb-4">
          <History className="h-6 w-6 text-kf-primary" />
          <h2 className="text-xl font-semibold">Versjonshistorikk</h2>
        </div>
        <p className="text-gray-500">Ingen godkjente versjoner ennå.</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex items-center space-x-3 mb-6">
        <History className="h-6 w-6 text-kf-primary" />
        <h2 className="text-xl font-semibold">Versjonshistorikk</h2>
        <span className="text-sm text-gray-500">
          ({versions.length} {versions.length === 1 ? 'versjon' : 'versjoner'})
        </span>
      </div>

      <div className="space-y-4">
        {versions.map((version, index) => {
          // Find first active (non-deleted) version - this is the current one
          const firstActiveIndex = versions.findIndex(v => !v.deleted_at);
          const isCurrentVersion = !version.deleted_at && index === firstActiveIndex;

          // Compact view for deactivated versions
          if (version.deleted_at) {
            return (
              <div
                key={version.version_id}
                className="border border-red-300 bg-red-50 rounded-lg p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-700">
                    Versjon {version.version_number}
                  </span>
                  <span className="px-2 py-1 text-xs font-medium bg-red-600 text-white rounded flex items-center gap-1">
                    <AlertTriangle size={12} />
                    Deaktivert
                  </span>
                  <span className="text-sm text-gray-600">
                    {formatDateTime(version.deleted_at)}
                  </span>
                </div>
                <button
                  onClick={() => navigate(`/verify/${version.approval_link}`)}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded hover:bg-gray-700 transition-colors"
                >
                  Vis versjon
                </button>
              </div>
            );
          }

          // Full view for active versions
          return (
            <div
              key={version.version_id}
              className={`border rounded-lg p-4 ${
                isCurrentVersion
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              } transition-colors`}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-lg">
                      Versjon {version.version_number}
                    </span>
                    {isCurrentVersion && (
                      <span className="px-2 py-1 text-xs font-medium bg-green-600 text-white rounded">
                        Gjeldende
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{version.title}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div className="flex items-center space-x-2 text-sm">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">
                    Godkjent: {formatDateTime(version.approved_at)}
                  </span>
                </div>

                <div className="flex items-center space-x-2 text-sm">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">
                    Av: {version.approver_name || 'Ukjent'}
                  </span>
                </div>
              </div>

              {version.change_comment && (
                <div className="mb-3 p-3 bg-kf-light border border-kf-primary-40 rounded">
                  <div className="flex items-start space-x-2">
                    <MessageSquare className="h-4 w-4 text-kf-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-kf-dark mb-1">
                        Endringskommentar:
                      </p>
                      <p className="text-sm text-kf-primary">{version.change_comment}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                <a
                  href={getVerificationUrl(version.approval_link)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-sm text-kf-primary hover:text-kf-primary hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Offentlig verifiseringslenke</span>
                </a>

                <div className="flex items-center gap-2">
                  {!isCurrentVersion && (
                    <button
                      onClick={() => navigate(`/verify/${version.approval_link}`)}
                      className="px-4 py-2 text-sm font-medium text-white bg-kf-primary rounded hover:bg-kf-dark transition-colors"
                    >
                      Vis denne versjonen
                    </button>
                  )}

                  {isSystemAdmin && (
                    <button
                      onClick={() => handleDeactivateClick(version)}
                      className="px-3 py-2 text-sm font-medium text-red-600 border border-red-600 rounded hover:bg-red-50 transition-colors flex items-center gap-1"
                      title="Deaktiver denne versjonen"
                    >
                      <Trash2 size={14} />
                      Deaktiver
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded border border-gray-200">
        <p className="text-sm text-gray-600">
          <strong>Merk:</strong> Alle godkjente versjoner lagres permanent. Hver versjon har en unik
          verifiseringslenke som kan brukes i kursbevis og dokumentasjon.
        </p>
      </div>

      {/* Deactivate Modal */}
      {showDeactivateModal && selectedVersion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="text-red-600" size={24} />
              <h3 className="text-xl font-bold">Deaktiver versjon</h3>
            </div>

            <p className="text-gray-600 mb-4">
              Er du sikker på at du vil deaktivere <strong>Versjon {selectedVersion.version_number}</strong>?
            </p>

            <div className="bg-kf-accent-20 border border-kf-accent-40 rounded p-3 mb-4">
              <p className="text-sm text-kf-accent">
                <strong>Advarsel:</strong> Denne versjonen vil ikke lenger være tilgjengelig via den offentlige
                verifiseringslenken. Dette kan ikke angres automatisk.
              </p>
            </div>

            <div className="mb-4">
              <label className="label text-sm font-medium mb-2">
                Årsak til deaktivering (valgfritt):
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-kf-primary"
                rows={3}
                placeholder="F.eks: Inneholdt feil informasjon, utdatert, erstattet av ny versjon..."
                value={deactivateReason}
                onChange={(e) => setDeactivateReason(e.target.value)}
                disabled={deactivating}
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleDeactivate}
                disabled={deactivating}
                className="btn btn-danger flex-1 flex items-center justify-center gap-2"
              >
                {deactivating ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Deaktiverer...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Deaktiver
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowDeactivateModal(false);
                  setSelectedVersion(null);
                  setDeactivateReason('');
                  setError(null);
                }}
                disabled={deactivating}
                className="btn btn-secondary flex-1"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
